import {
  Note,
  filenameToDate,
  filenameToKey,
  removeExtension,
  normalizeCalendarFilename,
} from '../note'
import { AuthType } from '../User'

/**
 * Pure functions for import conflict detection
 *
 * This module provides efficient, lazy conflict detection for markdown import.
 * Key principles:
 * - Pure functions with no side effects
 * - Lazy evaluation - only runs when files are imported
 * - Backend-agnostic - works with both Supabase and CloudKit
 * - Minimal memory allocation - direct iteration instead of Map creation
 */

export type ConflictResult = {
  hasConflict: boolean
  existingNote?: Note
}

/**
 * Detects conflicts for project notes using a pre-built filename map
 * Uses basename-only matching for efficient conflict detection
 * Performance: O(1) lookup instead of O(n) iteration
 *
 * @param filename - The filename to check for conflicts
 * @param filenameMap - Pre-built map of basename -> Note[] for the target folder
 * @returns ConflictResult indicating if conflict exists and the existing note
 */
export function detectProjectConflictWithMap(
  filename: string,
  filenameMap: Map<string, Note[]>
): ConflictResult {
  const basename = removeExtension(filename)
  const candidates = filenameMap.get(basename) ?? []

  // Return the first matching note (there should typically be only one per basename)
  if (candidates.length > 0) {
    return { hasConflict: true, existingNote: candidates[0] }
  }
  return { hasConflict: false }
}



/**
 * Detects conflicts for calendar notes using date-based keys with intelligent matching
 * Handles duplicate calendar notes for the same date by preferring:
 * 1. Exact basename match
 * 2. Normalized match (e.g., "2025-10-05" matches "20251005")
 * 3. Most recently modified note
 *
 * @param filename - The filename to check for conflicts
 * @param calendarNotes - Map of calendar notes grouped by date key
 * @returns ConflictResult indicating if conflict exists and the existing note
 */
export function detectCalendarConflict(
  filename: string,
  calendarNotes: Map<string, Note[]>
): ConflictResult {
  const key = filenameToKey(filename)

  if (!key) {
    return { hasConflict: false }
  }

  const notesForDate = calendarNotes.get(key) ?? []

  if (notesForDate.length === 0) {
    return { hasConflict: false }
  }

  const importBasename = removeExtension(filename)

  // Strategy 1: Prefer exact basename match
  let match = notesForDate.find(
    (note) => removeExtension(note.filename) === importBasename
  )

  // Strategy 2: Try normalized match
  if (!match) {
    const normalizedImport = normalizeCalendarFilename(filename).replace(
      /\.\w+$/,
      ''
    )
    match = notesForDate.find((note) => {
      const normalizedNote = normalizeCalendarFilename(note.filename).replace(
        /\.\w+$/,
        ''
      )
      return normalizedNote === normalizedImport
    })
  }

  // Strategy 3: Fall back to most recently modified
  if (!match) {
    let mostRecent = notesForDate[0]
    for (const note of notesForDate) {
      const noteModified = note.fileModifiedAt
        ? new Date(note.fileModifiedAt)
        : new Date(0)
      const mostRecentModified = mostRecent.fileModifiedAt
        ? new Date(mostRecent.fileModifiedAt)
        : new Date(0)

      if (noteModified > mostRecentModified) {
        mostRecent = note
      }
    }
    match = mostRecent
  }

  return {
    hasConflict: true,
    existingNote: match,
  }
}

/**
 * Optimized conflict detection function that uses a pre-built filename map
 * Automatically determines note type based on filename pattern and routes to appropriate detector
 * Performance: O(1) for project notes when using filename map
 *
 * @param filename - The filename to check for conflicts
 * @param targetFolder - Target folder path for project notes (used for calendar note fallback)
 * @param projectNotesFilenameMap - Pre-built filename map for the target folder
 * @param calendarNotes - Map of calendar notes grouped by date key
 * @param authType - Authentication type (used for calendar note fallback)
 * @returns ConflictResult indicating if conflict exists and the existing note
 */
export function detectConflictWithMap(
  filename: string,
  _targetFolder: string,
  projectNotesFilenameMap: Map<string, Note[]>,
  calendarNotes: Map<string, Note[]>,
  _authType: AuthType
): ConflictResult {
  const { date } = filenameToDate(filename)

  // Check calendar notes first if filename matches calendar pattern
  if (date) {
    return detectCalendarConflict(filename, calendarNotes)
  }

  // Otherwise check project notes using the pre-built filename map
  return detectProjectConflictWithMap(filename, projectNotesFilenameMap)
}
